import { TGenericAPIError } from '~/app/models'

export enum EScenarioKeys {
  id = 'id',
  name = 'name',
  triggerInstructions = 'triggerInstructions',
  additionalInstructions = 'additionalInstructions',
  agentCustomFields = 'agentCustomFields',
  actionIds = 'actionIds',
  channels = 'channels',
  instructionSteps = 'instructionSteps',
  instructionStep = 'InstructionStep',
}

export type TInstructionStep = {
  content: string
  id: string
  scenarioId: string
  stepNumber: number
}

export type TScenarioPayload = {
  [EScenarioKeys.name]: string
  [EScenarioKeys.instructionSteps]?: Record<number, string>
  [EScenarioKeys.instructionStep]?: TInstructionStep[]
  [EScenarioKeys.triggerInstructions]?: string
  [EScenarioKeys.additionalInstructions]?: string
  [EScenarioKeys.agentCustomFields]?: Record<string, TAgentCustomField>
  [EScenarioKeys.actionIds]?: string[]
  [EScenarioKeys.channels]?: EChannelKeys[]
}

export type TFormScenarioPayload = {
  [EScenarioKeys.name]: string
  [EScenarioKeys.instructionSteps]?: string
  [EScenarioKeys.triggerInstructions]?: string
  [EScenarioKeys.additionalInstructions]?: string
  [EScenarioKeys.agentCustomFields]?: Record<string, TAgentCustomField>
  [EScenarioKeys.actionIds]?: string[]
  [EScenarioKeys.channels]?: EChannelKeys[]
}

export type TUpdateScenarioTitlePayload = {
  [EScenarioKeys.name]: string
}

export enum EFormAgentCustomFieldKeys {
  key = 'key',
  type = 'type',
  description = 'description',
  required = 'required',
}

export type TFormAgentCustomField = TAgentCustomField & {
  [EFormAgentCustomFieldKeys.key]: string
}

export type TUpdateScenarioPayload = Partial<
  Omit<
    TScenarioPayload,
    | EScenarioKeys.name
    | EScenarioKeys.instructionSteps
    | EScenarioKeys.agentCustomFields
  > & {
    instructionSteps: string
    agentCustomFields: TFormAgentCustomField[]
  }
>

export type TScenario = {
  [EScenarioKeys.id]: string
} & Omit<TScenarioPayload, EScenarioKeys.instructionSteps>

export type TScenarioError = TGenericAPIError

export enum EActionKeys {
  id = 'id',
  name = 'name',
  parametersList = 'parametersList',
  waitMessage = 'waitMessage',
  allPropertiesRequired = 'allPropertiesRequired',
  useTemplate = 'useTemplate',
  webhook = 'webhook',
  description = 'description',
}

export type TScenarioAgentCustomField = string | boolean | number

export type TScenarioAgentCustomFields = // the metadata table.
  Record<string, TScenarioAgentCustomField>

export type TAgentCustomField = {
  [EFormAgentCustomFieldKeys.type]: TScenarioAgentCustomField
  [EFormAgentCustomFieldKeys.description]: string
  [EFormAgentCustomFieldKeys.required]: boolean
}

export type TAction = {
  [EActionKeys.id]: string
  [EActionKeys.name]: string
  [EActionKeys.parametersList]: {
    [key: string]: TAgentCustomField
  }
  [EActionKeys.waitMessage]: string
  [EActionKeys.allPropertiesRequired]: boolean
  [EActionKeys.useTemplate]: string
  [EActionKeys.webhook]: string
  [EActionKeys.description]: string
}

export enum EChannelKeys {
  phone = 'phone',
  email = 'email',
  text = 'text',
  chat = 'chat',
}

export const ChannelFriendlyNames: Record<EChannelKeys, string> = {
  [EChannelKeys.phone]: 'Phone',
  [EChannelKeys.email]: 'Email',
  [EChannelKeys.text]: 'Text',
  [EChannelKeys.chat]: 'Chat',
}

export enum EScenarioAgentKeys {
  id = 'id',
  scenarioId = 'scenarioId',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  active = 'active',
  agentId = 'agentId',
  customFields = 'customFields',
  timezone = 'timezone',
  Agent = 'Agent',
}

export type TScenarioAgentAgent = {
  agentReferenceName: string
}

export type TScenarioAgent = {
  [EScenarioAgentKeys.id]: string
  [EScenarioAgentKeys.scenarioId]: string
  [EScenarioAgentKeys.createdDate]: string
  [EScenarioAgentKeys.updatedDate]: string
  [EScenarioAgentKeys.active]: boolean
  [EScenarioAgentKeys.agentId]: string
  [EScenarioAgentKeys.customFields]: TScenarioAgentCustomFields | null
  [EScenarioAgentKeys.timezone]: string | null
  [EScenarioAgentKeys.Agent]: TScenarioAgentAgent | null
}

export type TUpdateScenarioAgentPayload = Omit<
  TScenarioAgent,
  | EScenarioAgentKeys.id
  | EScenarioAgentKeys.agentId
  | EScenarioAgentKeys.scenarioId
  | EScenarioAgentKeys.createdDate
  | EScenarioAgentKeys.updatedDate
  | EScenarioAgentKeys.Agent
>

export enum EScenarioActionKeys {
  id = 'id',
  name = 'name',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  parametersList = 'parametersList',
  waitMessage = 'waitMessage',
  allPropertiesRequired = 'allPropertiesRequired',
  usesTemplate = 'usesTemplate',
  webhook = 'webhook',
  description = 'description',
  failureMessage = 'failureMessage',
  method = 'httpMethod',
  authHeader = 'authHeader',
  apiKey = 'apiKey',
  apiCredentialId = 'apiCredentialId',
  headers = 'headers',
}

export enum EScenarioActionUsesTemplate {
  YES = 'yes',
  NO = 'no',
}

type TParameterListPrimitiveType = 'string' | 'number' | 'boolean'

export type TParameterListType =
  | TParameterListPrimitiveType
  | [TParameterListPrimitiveType, 'null']

export enum EParameterListKeys {
  type = 'type',
  description = 'description',
  required = 'required',
}

export type TParameterListContent = {
  [EParameterListKeys.type]: TParameterListType
  [EParameterListKeys.description]: string
  [EParameterListKeys.required]: boolean
}

export enum EScenarioActionMethod {
  POST = 'POST',
  GET = 'GET',
  DELETE = 'DELETE',
}

export type TScenarioActionPayload = {
  [EScenarioActionKeys.name]: string
  [EScenarioActionKeys.parametersList]: Record<string, TParameterListContent>
  [EScenarioActionKeys.waitMessage]: string
  [EScenarioActionKeys.allPropertiesRequired]: boolean
  [EScenarioActionKeys.usesTemplate]: EScenarioActionUsesTemplate
  [EScenarioActionKeys.webhook]: string
  [EScenarioActionKeys.description]: string
  [EScenarioActionKeys.failureMessage]: string
  [EScenarioActionKeys.method]?: EScenarioActionMethod
  [EScenarioActionKeys.authHeader]: string
  [EScenarioActionKeys.apiKey]: string
  [EScenarioActionKeys.apiCredentialId]?: string
  [EScenarioActionKeys.headers]?: string
}

export enum EFormParameterListKeys {
  id = 'id',
  name = 'name',
  description = 'description',
  type = 'type',
  isOptional = 'isOptional',
}

export type TFormParameterListType = TParameterListPrimitiveType

export type TFormParameterListContent = {
  [EFormParameterListKeys.id]: string
  [EFormParameterListKeys.name]: string
  [EFormParameterListKeys.description]: string
  [EFormParameterListKeys.type]: TFormParameterListType
  [EFormParameterListKeys.isOptional]: boolean
}

export type TFormScenarioActionPayload = Omit<
  TScenarioActionPayload,
  | EScenarioActionKeys.parametersList
  | EScenarioActionKeys.method
  | EScenarioActionKeys.headers
> & {
  [EScenarioActionKeys.parametersList]: TFormParameterListContent[]
  [EScenarioActionKeys.method]: EScenarioActionMethod | undefined
  [EScenarioActionKeys.headers]: { key: string; value: string }[]
}

export type TUpdateScenarioActionTitlePayload = {
  [EScenarioActionKeys.name]: string
}

export type TScenarioAction = {
  [EScenarioActionKeys.id]: string
} & TScenarioPayload

export type TScenarioActionError = TGenericAPIError
